<template>
  <div class="wrapper">
    <a-form-model
      layout="horizontal"
      :model="form"
      :rules="rules"
      ref="formRef"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <a-form-model-item :colon="false" label-align="left" label="Associate" prop="associate_id">
        <search-associates :is-associate-id="true" :multiply="true" :value.sync="form.associate_id" />
      </a-form-model-item>

      <a-alert message="Select the start and end dates for finding associate's shifts" type="info" show-icon />
      <a-form-model-item :colon="false" label-align="left" label="Date Range" prop="dates">
        <a-range-picker v-model="form.dates" separator="-" format="MM/DD/YYYY"> </a-range-picker>
      </a-form-model-item>
    </a-form-model>

    <a-table
      :row-selection="{
        selectedRowKeys: selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: record => ({
          props: { disabled: record.status === 'deleted' }
        })
      }"
      :rowKey="record => record.id"
      :locale="{ emptyText: 'Shifts Not Found' }"
      :loading="loadingShifts"
      size="small"
      :pagination="pagination"
      :columns="columns"
      :data-source="associateShifts"
      :scroll="{ y: 200, x: 800 }"
      @change="handleTableChange"
    >
      <span slot="associate" slot-scope="text, record">
        <grouped-associate-info
          :first-name="record.internal_payment_info.associate.first_name"
          :last-name="record.internal_payment_info.associate.last_name"
          :photo-url="record.internal_payment_info.associate.photo_url"
          :unverified="!record.internal_payment_info.associate.is_verified"
          :role="record.internal_payment_info.associate.role.name"
          :associate-number="record.internal_payment_info.associate.associate_number"
          :scheduled-type="record.internal_payment_info.associate.schedule_type"
        />
      </span>
      <span slot="date" slot-scope="text, record">
        {{ formatDate(record.scheduled_start) }} -
        {{ formatDate(record.scheduled_end) }}
      </span>
      <span slot="status" slot-scope="text, record">
        <a-tooltip>
          <template slot="title">
            {{ getShiftStatusTagText(record.status) }}
          </template>
          <a-tag :color="getAuthStatusTagColor(record.status)">
            {{ getShiftStatusTagLabel(record.status) }}
          </a-tag>
        </a-tooltip>
      </span>
      <template slot="footer">
        <span v-if="hasSelected">{{ `Selected ${selectedRowKeys.length} shifts` }}</span>
      </template>
    </a-table>

    <a-form-model-item :labelCol="{ span: 0 }" :colon="false" label-align="left" prop="shift_ids"> </a-form-model-item>
  </div>
</template>

<script>
import { getAuthStatusTagColor, getShiftStatusTagLabel, getShiftStatusTagText } from '@/const'
import { Alert, DatePicker, FormModel, notification, Table, Tag, Tooltip } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import shiftsHelper from '@/helpers/shifts'
import SearchAssociates from '@/components/search-associates.vue'
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'

const { mapActions: shiftActions } = createNamespacedHelpers('shifts')

const initialPagination = {
  pageSizeOptions: ['5', '10', '15'],
  showSizeChanger: true,
  pageSize: 5,
  current: 1,
  total: 0
}

export default {
  components: {
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-alert': Alert,
    'a-range-picker': DatePicker.RangePicker,
    'a-tag': Tag,
    'a-tooltip': Tooltip,
    'a-table': Table,
    'grouped-associate-info': GroupedAssociateInfo,
    'search-associates': SearchAssociates
  },
  props: {
    shift_ids: {
      type: Array,
      default() {
        return []
      }
    },
    initialValues: {
      type: Object,
      default: undefined
    }
  },
  watch: {
    form: {
      handler(newVal) {
        this.clearSelection()

        if (newVal.associate_id.length && newVal.dates.length > 0) {
          this.fetchShifts()
        }
      },
      deep: true,
      immediate: true
    },
    initialValues: {
      handler(newVal) {
        if (!newVal) {
          return
        }

        if (newVal.associate_id) this.form.associate_id = [newVal.associate_id]

        let start = newVal.start ?? null
        let end = newVal.end ?? null

        if (!start && end) {
          start = end
        } else if (!end && start) {
          end = start
        }

        this.form.dates = [start, end]
      },
      immediate: true,
      deep: true
    }
  },
  mixins: [shiftsHelper],
  data() {
    return {
      form: {
        associate_id: [],
        dates: []
      },

      associateShifts: [],
      selectedRowKeys: [],
      selectedShiftRecords: {},

      pagination: initialPagination,

      rules: {
        associate_id: [
          {
            required: true,
            message: 'Please select associates',
            trigger: 'change',
            type: 'array'
          }
        ],
        dates: [
          {
            required: true,
            message: 'Please select date range',
            trigger: 'change',
            type: 'array'
          }
        ]
      },

      loadingAssociated: false,
      loadingShifts: false,

      columns: [
        {
          title: 'Shift #',
          dataIndex: 'id',
          width: 100
        },
        {
          title: 'Associate',
          dataIndex: 'name',
          key: 'associate',
          width: 300,
          scopedSlots: { customRender: 'associate' }
        },
        {
          title: 'Date',
          scopedSlots: { customRender: 'date' },
          width: 200
        },
        {
          title: 'Status',
          scopedSlots: { customRender: 'status' },
          width: 200
        }
      ],
      requestKey: `shifts-table-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    }
  },
  computed: {
    hasSelected() {
      return this.selectedRowKeys.length > 0
    }
  },
  methods: {
    getAuthStatusTagColor,
    getShiftStatusTagLabel,
    getShiftStatusTagText,
    ...shiftActions(['getShifts']),

    formatDate(date) {
      return moment(date).format(this.$store.state.applicationState.dateFormat)
    },

    // work with shifts table

    clearSelection() {
      this.selectedRowKeys = []
      this.selectedShiftRecords = {}
      this.$emit('update:shift_ids', [])
      this.$emit('selected-shifts', [])
    },

    onSelectChange(selectedRowKeys, selectedRows) {
      const records = { ...this.selectedShiftRecords }

      this.associateShifts.forEach(shift => delete records[shift.id])
      selectedRows.forEach(shift => {
        records[shift.id] = shift
      })

      this.selectedShiftRecords = records
      this.selectedRowKeys = Object.keys(records).map(Number)
      this.$emit('update:shift_ids', this.selectedRowKeys)
      this.$emit(
        'selected-shifts',
        this.selectedRowKeys.map(id => records[id])
      )
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }

      this.fetchShifts()
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    // work witch shifts (attached)

    async fetchShifts() {
      this.loadingShifts = true
      let skipFinally = false

      try {
        const valid = await new Promise(resolve => {
          this.$refs.formRef.validate(resolve)
        })

        if (!valid) {
          this.showNotification('warning', 'Validation Error', 'Please fill in all required fields correctly.')

          return
        }

        const payload = {
          filter: {
            shift_type: 'worked',
            associates: this.form.associate_id,
            start_date: moment(this.form.dates[0]).startOf('day').utc(true).toISOString(),
            end_date: moment(this.form.dates[1]).startOf('day').utc(true).toISOString()
          },
          page: this.pagination.current,
          size: this.pagination.pageSize,
          fields: '{id,status,scheduled_start,scheduled_end,internal_payment_info{associate}}',
          requestGroup: this.requestKey
        }

        const { items, total_count } = await this.getShifts(payload)
        this.associateShifts = items
        this.pagination.total = total_count
      } catch (error) {
        if (error === 'canceled') {
          skipFinally = true
          this.loadingShifts = true
          return
        }
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        if (!skipFinally) this.loadingShifts = false
      }
    }
  }
}
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-calendar-picker {
  margin: 0;
  width: 100%;
}
</style>
